// The boss registry: which bosses exist and which village gets which.
// Adding a boss = one file in sim/bosses/ + ONE line in BOSSES below (+ its client view's line in
// client/render/run/bosses/index.ts). See sim/bosses/README.md.

import type { EType } from '../defs';
import { BossDef, BossKind } from './types';
import { CERBERUS } from './cerberus';
import { BULWARK_CUBE } from './bulwark';
import { ANGLER } from './angler';
import { BLACK_COURT } from './court';
import { LEVIATHAN } from './leviathan';

type AnyBoss = BossDef<any>;

/** every ported boss, by kind */
export const BOSSES: Partial<Record<BossKind, AnyBoss>> = {
  [BossKind.Cerberus]: CERBERUS,
  [BossKind.BulwarkCube]: BULWARK_CUBE,
  [BossKind.Angler]: ANGLER,
  [BossKind.BlackCourt]: BLACK_COURT,
  [BossKind.Leviathan]: LEVIATHAN,
};

/** The village rotation (GAMEPLAY_LOOP §5.4's Godot ladder Cube → Chess → Cerberus → Angler, with
 *  Cerberus pulled to village 1 because the browser build opened with him). Cycles past the end.
 *  A kind not ported yet is skipped: the next ported one in the cycle takes its village. */
export const ROTATION: BossKind[] = [BossKind.Cerberus, BossKind.BulwarkCube, BossKind.BlackCourt, BossKind.Angler];

/** The extraction pursuer (BOSS_GRAMMAR §4b): never an island boss, never in ROTATION. It rises when
 *  the bell is rung (sim/run.ts ringBell → sim/bosses/leviathan.ts summonLeviathan). EMG_BOSS=leviathan
 *  skips the island boss and drops the posse straight into the hold with it. */
export const PURSUER = BossKind.Leviathan;

export function bossDef(kind: BossKind): AnyBoss {
  return BOSSES[kind] ?? CERBERUS;
}

export function bossForVillage(village: number): BossKind {
  const n = ROTATION.length;
  for (let k = 0; k < n; k++) {
    const kind = ROTATION[(((village + k) % n) + n) % n];
    if (BOSSES[kind]) return kind;
  }
  return BossKind.Cerberus;
}

/** dev / lab names (EMG_BOSS=cube, run.html?boss=cube) */
export const BOSS_KEYS: Record<string, BossKind> = {
  cerberus: BossKind.Cerberus, cube: BossKind.BulwarkCube, bulwark: BossKind.BulwarkCube,
  court: BossKind.BlackCourt, chess: BossKind.BlackCourt, angler: BossKind.Angler, fish: BossKind.Angler, leviathan: BossKind.Leviathan,
};
export function bossKey(name: string | undefined | null): BossKind {
  const k = name ? BOSS_KEYS[name.toLowerCase()] ?? (Number(name) || 0) : 0;
  return k && BOSSES[k as BossKind] ? (k as BossKind) : BossKind.None;
}

/** is this enemy type one of a boss's bodies (skipped by the robot loops)? */
export function bossOwns(kind: BossKind, type: EType) {
  return bossDef(kind).types.includes(type);
}
